import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import {
  AlertCircle,
  Building2,
  ChevronRight,
  LayoutDashboard,
  Megaphone,
  Sparkles,
  UserPlus,
  Users as UsersIcon,
} from 'lucide-react';
import { db } from '../../firebase';
import {
  ROLES,
  ROLE_LABELS,
  SUBSCRIPTION_STATUS,
  resolvePlanTier,
} from '../../constants/roles';
import { resolveLifecycleState, BUILD_STATUS } from '../../constants/buildStatus';
import {
  ANNOUNCEMENTS_COLLECTION,
  resolveAnnouncement,
  isWithinScheduleWindow,
} from '../../constants/announcements';
import { useAdminData } from '../../components/admin/AdminLayout';

function StatCard({ icon: Icon, label, value, hint, to }) {
  const body = (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex items-start gap-4 hover:border-slate-300 transition-colors">
      <div className="p-2 rounded-lg bg-slate-100 text-slate-700"><Icon size={18} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
        <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
        {hint ? <div className="text-[11px] text-slate-500 mt-1">{hint}</div> : null}
      </div>
      {to ? <ChevronRight size={16} className="text-slate-300" /> : null}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function AdminOverview() {
  const { users, farms } = useAdminData();
  const [activeAnnouncements, setActiveAnnouncements] = useState([]);

  useEffect(() => {
    const q1 = query(
      collection(db, ANNOUNCEMENTS_COLLECTION),
      where('enabled', '==', true),
    );
    const unsub = onSnapshot(
      q1,
      (snap) => {
        const rows = snap.docs.map((d) => resolveAnnouncement(d.id, d.data()));
        const live = rows.filter((a) => isWithinScheduleWindow(a));
        live.sort((a, b) => b.priority - a.priority);
        setActiveAnnouncements(live);
      },
      () => {
        setActiveAnnouncements([]);
      },
    );
    return () => unsub();
  }, []);

  const stats = useMemo(() => {
    const totalUsers = users.length;
    const totalFarms = farms.length;
    const errorFarms = farms.filter((f) => {
      const lc = resolveLifecycleState(f);
      return lc.key === BUILD_STATUS.BUILD_FAILED || (f.status || '').toUpperCase() === 'ERROR';
    });
    const stale30d = farms.filter((f) => {
      if (!f.lastUpdate) return true;
      const t = Date.parse(f.lastUpdate);
      if (!Number.isFinite(t)) return false;
      return Date.now() - t > 30 * 24 * 60 * 60 * 1000;
    });
    const paid = users.filter((u) => resolvePlanTier(u) === 'paid');
    const overrides = users.filter((u) => u.subscriptionOverride === true);
    return { totalUsers, totalFarms, errorFarms, stale30d, paid, overrides };
  }, [users, farms]);

  const recentSignups = useMemo(() => {
    return [...users]
      .filter((u) => u.createdAt)
      .sort((a, b) => {
        const ta = Date.parse(a.createdAt) || 0;
        const tb = Date.parse(b.createdAt) || 0;
        return tb - ta;
      })
      .slice(0, 10);
  }, [users]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3 text-slate-900">
            <LayoutDashboard size={22} /> Admin overview
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Quick health snapshot. Click any tile to dive into the underlying section.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={UsersIcon}
          label="Users"
          value={stats.totalUsers}
          hint={`${stats.paid.length} paid · ${stats.overrides.length} override`}
          to="/admin/users"
        />
        <StatCard
          icon={Building2}
          label="Properties"
          value={stats.totalFarms}
          hint={`${stats.stale30d.length} not updated in 30+ days`}
          to="/admin/properties"
        />
        <StatCard
          icon={AlertCircle}
          label="Properties in error"
          value={stats.errorFarms.length}
          hint={stats.errorFarms.length > 0 ? 'Click to triage in Properties' : 'Everything is healthy'}
          to="/admin/properties"
        />
        <StatCard
          icon={Megaphone}
          label="Active announcements"
          value={activeAnnouncements.length}
          hint={activeAnnouncements.length > 0 ? `${activeAnnouncements[0].variant} · priority ${activeAnnouncements[0].priority}` : 'No live announcements'}
          to="/admin/announcements"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <span className="font-bold text-sm text-slate-700 flex items-center gap-2">
              <AlertCircle size={16} className="text-slate-500" /> Properties needing attention
            </span>
            <Link to="/admin/properties" className="text-[11px] text-[#043132] hover:underline">View all</Link>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
            {stats.errorFarms.length === 0 && stats.stale30d.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">All properties look healthy.</div>
            ) : null}
            {stats.errorFarms.map((f) => {
              const lc = resolveLifecycleState(f);
              return (
                <Link key={f.id} to="/admin/properties" className="flex items-center justify-between p-3 hover:bg-slate-50">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 text-sm truncate">{f.farmName || f.name || f.id}</div>
                    <div className="text-[10px] text-slate-400 font-mono truncate">{f.id}</div>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border border-current/20 ${lc.pillClass || 'bg-rose-50'} ${lc.textClass || 'text-rose-700'}`}>
                    {lc.label}
                  </span>
                </Link>
              );
            })}
            {stats.stale30d.slice(0, 8).map((f) => (
              <div key={`stale-${f.id}`} className="flex items-center justify-between p-3 hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 text-sm truncate">{f.farmName || f.name || f.id}</div>
                  <div className="text-[10px] text-slate-400">
                    {f.lastUpdate ? `Last update ${new Date(f.lastUpdate).toLocaleDateString()}` : 'Never updated'}
                  </div>
                </div>
                <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium border border-amber-200">
                  Stale
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <span className="font-bold text-sm text-slate-700 flex items-center gap-2">
              <UserPlus size={16} className="text-slate-500" /> Recent sign-ups
            </span>
            <Link to="/admin/users" className="text-[11px] text-[#043132] hover:underline">View all</Link>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
            {recentSignups.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">No sign-ups recorded yet.</div>
            ) : null}
            {recentSignups.map((u) => (
              <Link key={u.id} to="/admin/users" className="flex items-center justify-between p-3 hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 text-sm truncate">{u.businessName || u.displayName || u.email}</div>
                  <div className="text-[10px] text-slate-400 truncate">{u.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] text-slate-500">{ROLE_LABELS[u.role] || ROLE_LABELS[ROLES.USER]}</div>
                  <div className="text-[10px] text-slate-400">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold text-sm text-slate-700 flex items-center gap-2">
            <Sparkles size={16} className="text-slate-500" /> Communication state
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-lg p-3 text-sm">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Announcements</div>
            {activeAnnouncements.length === 0 ? (
              <div className="text-slate-500">No live announcement.</div>
            ) : (
              <ul className="space-y-1">
                {activeAnnouncements.slice(0, 3).map((a) => (
                  <li key={a.id} className="flex items-start justify-between gap-2">
                    <span className="truncate text-slate-700">{a.message || '(no message)'}</span>
                    <span className="text-[10px] text-slate-500 shrink-0">{a.target.mode === 'all' ? 'All users' : `${a.target.uids.length} user(s)`}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/admin/announcements" className="text-[11px] text-[#043132] hover:underline mt-2 inline-block">Manage announcements →</Link>
          </div>
          <div className="border border-slate-200 rounded-lg p-3 text-sm">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Promo banner</div>
            <div className="text-slate-500">
              An active announcement overrides the promo banner; turn announcements off to fall back to the coupon banner.
            </div>
            <Link to="/admin/promo-banner" className="text-[11px] text-[#043132] hover:underline mt-2 inline-block">Edit promo banner →</Link>
          </div>
        </div>
      </div>

      {/* Unsubscribed but active users (admin-attention list) */}
      {users.some((u) => u.subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE) ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div>
            One or more users have subscriptions in <strong>past due</strong> status. <Link to="/admin/users" className="underline">Review in Users →</Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
